/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Grid, makeStyles } from '@material-ui/core';
import { AlertDialog, LoaderBar, Page } from 'src/components';
import { SupabaseClientService } from 'src/services';
import Toolbar from './Toolbar';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import {
  TapTapCustomerData,
  TapTapCustomerPayment
} from 'src/redux/slices/taptap-customer/types';
import AddCustomerPaymentDialog from './AddCustomerPaymentDialog';
import TapTapCustomerPaymentDetailsDialog from './TapTapCustomerPaymentDetailsDialog';
import {
  currentTapTapVersion,
  findSelectedQuickFilterViaParamV2,
  getQuickFilterByLabel,
  getTapTapQuickViewFiltersViaPermissionV2,
  quickFilterViewsLabels,
  fetchTicketLogsByStatuses,
  taptapGetProfilesThunk,
  branchIdToCode
} from 'src/redux/slices/taptap-customer';
import { taptapCustomerField } from 'src/redux/slices/taptap-customer/constants/taptap-fields';
import {
  STATUS_BY_VIEW,
  superiorTapTapRoles
} from 'src/redux/slices/taptap-customer/constants/taptap-flow-process';
import Summary from './components/TapTapSummary';
import TapTapForBuildCustomerItem from './components/TapTapForBuildCustomerItem';
import { usePermissions } from 'src/hooks';
import { cloneDeep } from 'lodash';
import { useBranchInfo } from 'src/hooks/branch/use-branch-info';
import { TapTapCashierView } from './views/TapTapCashierView';
import useUserInfo from 'src/hooks/user/use-user-info';
import { useTaptapFlow } from 'src/hooks/taptap/use-tap-tap-flow';
import { TapTapAfterCareQAView } from './views/TapTapAfterCareQAView';
import { TapTapAccountingView } from './views/TapTapAccountingView';
import { TapTapAdminDashboard } from './views/TapTapAdminDashboard';
import { TapTapReleasingView } from './views/TapTapReleasingView';
import { TapTapSalesDashboardView } from './views/TapTapSalesDashboardView';
import { OperationDashboard } from './views/OperationDashboard';
import { convertToLocalISO, getDateRangePreset } from 'src/utils';
import { PermissionApiName } from 'src/types';

const useStyles = makeStyles(() => ({
  root: {},
  menuItem: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  clearText: {
    color: 'rgb(244, 67, 54)',
    display: 'flex',
    justifyContent: 'space-between'
  },
  statusContainer: {
    flexDirection: 'column',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
}));

const {
  selectors: taptapCustomerSelectors,
  actions: taptapCustomerActions
} = slices.taptapCustomer;

const WRITABLE_TAPTAP_DASHBOARD_PERMISSIONS: PermissionApiName[] = [
  'can_view_taptap_sales_dashboard',
  'can_view_taptap_cashier_dashboard',
  'can_view_taptap_accounting_dashboard',
  'can_view_after_care_dashboard',
  'can_view_taptap_build_dashboard',
  'can_view_taptap_warehouse_dashboard',
  'can_view_all_taptap_dashboard'
];

const getTodayCreatedAtConditions = () => {
  const range = getDateRangePreset('today');

  if (!range) {
    return [];
  }

  return [
    {
      field: taptapCustomerField.CREATED_AT,
      operator: 'gte',
      value: convertToLocalISO(range.start)
    },
    {
      field: taptapCustomerField.CREATED_AT,
      operator: 'lte',
      value: convertToLocalISO(range.end, true)
    }
  ];
};

const TapTapViewV2 = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { defaultBranch, getBranches } = useBranchInfo();
  const {
    userPermissionsStringArray,
    canViewAllTaptapDashboard,
    readOnlyTaptapAdminDashboard
  } = usePermissions();
  const { userDetails } = useUserInfo();
  const { roleBasedViewLabel } = useTaptapFlow();

  const customers = useAppSelector(taptapCustomerSelectors.selectCustomers);
  const isCustomersLoading = useAppSelector(
    taptapCustomerSelectors.selectTaptapCustomerLoading
  );
  const taptapVersion = useAppSelector(
    taptapCustomerSelectors.selectTapTapVersion
  );
  const isNextPageLoading = useAppSelector(
    taptapCustomerSelectors.selectTaptapCustomerNextPageLoading
  );
  const selectedFilter = useAppSelector(
    taptapCustomerSelectors.selectTaptapCustomerFilter
  );
  const selectedGetCustomerPayload = useAppSelector(
    taptapCustomerSelectors.selectTaptapCustomerPayload
  );

  const [
    addCustomerPaymentViaCustomerIDDialogVisible,
    setAddCustomerPaymentViaCustomerIDDialogVisible
  ] = useState<TapTapCustomerData>();

  const [
    editCustomerPaymentViaCustomerIDDialogVisible,
    setEditCustomerPaymentViaCustomerIDDialogVisible
  ] = useState<TapTapCustomerPayment>();

  const [statusFilter, setStatusFilter] = useState<string>('');
  const [courierFilter, setCourierFilter] = useState<string>('');
  const [showUi, setShowUi] = useState<boolean>(false);
  const [selectedDashboardView, setSelectedDashboardView] = useState<
    string | null
  >(null);

  const [runningDurationLogMap, setRunningDurationLogMap] = useState<
    // eslint-disable-next-line no-undef
    Record<string, string>
  >({});

  const canViewAllDashboard = useMemo(
    () =>
      canViewAllTaptapDashboard ||
      readOnlyTaptapAdminDashboard ||
      userDetails?.all_roles?.some((role) =>
        superiorTapTapRoles.includes(role.name ?? '')
      ),
    [canViewAllTaptapDashboard, readOnlyTaptapAdminDashboard, userDetails]
  );

  const hasWritableTaptapDashboardPermission = useMemo(
    () =>
      WRITABLE_TAPTAP_DASHBOARD_PERMISSIONS.some((permission) =>
        userPermissionsStringArray.includes(permission)
      ),
    [userPermissionsStringArray]
  );

  const isTaptapDashboardReadOnly =
    readOnlyTaptapAdminDashboard && !hasWritableTaptapDashboardPermission;

  const filteredCustomers = useMemo(() => {
    let baseList = statusFilter
      ? customers.filter((customer) => customer.status === statusFilter)
      : customers;

    if (courierFilter) {
      baseList = baseList.filter(
        (customer) => customer.courier === courierFilter
      );
    }

    return baseList;
  }, [statusFilter, courierFilter, customers]);

  const hasCustomers = useMemo(() => !!customers[0], [customers]);

  /** ============================
   * START OF SUPABASE STUFF
   ============================ */

  //  TODO: Fix any
  const onUpdateLocalCustomerPaymentDetail = async (data: any) => {
    const updatedCustomerPaymentData: TapTapCustomerPayment = data?.new ?? {};
    dispatch(
      taptapCustomerActions.updateTaptapCustomerPaymentDetails(
        updatedCustomerPaymentData
      )
    );
  };

  const onDeleteLocalCustomerPayment = (data: any) => {
    const deletedCustomerData: TapTapCustomerData = data?.old ?? {};
    dispatch(
      taptapCustomerActions.removeTaptapCustomerPayment(deletedCustomerData)
    );
  };

  //  TODO: Fix any
  const onUpdateLocalCustomerDetail = async (data: any) => {
    const updatedCustomerData: TapTapCustomerData = data?.new ?? {};
    dispatch(taptapCustomerActions.updateTaptapCustomer(updatedCustomerData));
  };

  //  TODO: Fix any
  const onUpdateLocalCustomerPaymentList = async (data: any) => {
    const newCustomerPaymentData: TapTapCustomerPayment = data?.new ?? {};

    dispatch(
      taptapCustomerActions.insertNewTaptapCustomerPayment(
        newCustomerPaymentData
      )
    );
  };

  const onInsertOnLocalCustomerList = async (data: any) => {
    const newCustomerData: TapTapCustomerData = data?.new ?? {};
    dispatch(taptapCustomerActions.insertNewTaptapCustomers(newCustomerData));
  };

  const onDeleteLocalCustomer = (data: any) => {
    const deletedCustomerData: TapTapCustomerData = data?.old ?? {};
    dispatch(taptapCustomerActions.removeTaptapCustomer(deletedCustomerData));
  };

  /** ============================
   * END OF SUPABASE STUFF
   ============================ */

  /** ============================
   * START OF COMPONENT STUFF
   ============================ */

  const fetchCustomers = (
    isInitialApiCall?: boolean,
    overrideLabel?: string
  ) => {
    if (isInitialApiCall) {
      const fallbackView = getTapTapQuickViewFiltersViaPermissionV2(
        userPermissionsStringArray
      )?.[0];

      const selectedView = getQuickFilterByLabel(overrideLabel) || fallbackView;

      if (!selectedView) {
        alert('You have no permission to view taptap customers!');
        window.location.href = '/';
        return;
      }
      setShowUi(true);
      setSelectedDashboardView(selectedView.label);
      const cloned = cloneDeep(selectedGetCustomerPayload);

      cloned.includeAllTicketStatuses = true;
      cloned.filters = {
        logic: 'and',
        conditions: []
      };
      cloned.filters.conditions.push(...getTodayCreatedAtConditions());

      // Use the user's first assigned branch as the initial dashboard branch.
      if (defaultBranch?.value) {
        //PRODUCTION DEFAULT: WAG TUTULARAN
        const branchCode = branchIdToCode[defaultBranch.value];
        if (branchCode) {
          cloned.filters.conditions.push({
            field: 'branch_released',
            operator: 'in',
            value: [branchCode]
          });
        }
      }

      cloned.sort = selectedView.sort;
      dispatch(taptapCustomerActions.updateTaptapCustomerGetPayload(cloned));
    }

    dispatch(taptapCustomerActions.taptapFetchCustomersThunk());
  };

  const fetchCustomersNextPage = () => {
    if (isCustomersLoading || isNextPageLoading) {
      return;
    }

    dispatch(taptapCustomerActions.taptapFetchCustomersNextPageThunk());
  };

  const fetchTapTapProfiles = () => {
    dispatch(taptapGetProfilesThunk());
  };

  /** ============================
   * END OF COMPONENT STUFF
   ============================ */

  //Realtime changes here
  useEffect(() => {
    // getBranches();
    // fetchCustomers(true);
    // fetchTapTapProfiles();

    const customerTableChanges = SupabaseClientService.channel(
      'customers_channel'
    ) // TODO: constantize this
      .on(
        'postgres_changes', // TODO: constantize this
        {
          event: '*', // you can specify 'INSERT', 'UPDATE', 'DELETE' etc. // TODO: constantize this
          schema: 'public', // TODO: constantize this
          table: 'customers' // TODO: constantize this
        },
        (payload) => {
          if (payload?.table === 'customers') {
            if (payload?.eventType === 'INSERT') {
              onInsertOnLocalCustomerList(payload);
            }
            if (payload?.eventType === 'UPDATE') {
              onUpdateLocalCustomerDetail(payload);
            }
            if (payload?.eventType === 'DELETE') {
              onDeleteLocalCustomer(payload);
            }
          }
        }
      )
      .subscribe();

    const customersPaymentsTableChanges = SupabaseClientService.channel(
      'customers_payments_channel'
    ) // TODO: constantize this
      .on(
        'postgres_changes', // TODO: constantize this
        {
          event: '*', // you can specify 'INSERT', 'UPDATE', 'DELETE' etc. // TODO: constantize this
          schema: 'public', // TODO: constantize this
          table: 'customers_payments'
        },
        (payload) => {
          if (payload?.table === 'customers_payments') {
            if (payload?.eventType === 'INSERT') {
              onUpdateLocalCustomerPaymentList(payload);
            }
            if (payload?.eventType === 'UPDATE') {
              // onUpdateLocalCustomerDetail(payload);
              onUpdateLocalCustomerPaymentDetail(payload);
            }
            if (payload?.eventType === 'DELETE') {
              // TODO: Implement this
              onDeleteLocalCustomerPayment(payload);
            }
          }
        }
      )
      .subscribe();

    // Cleanup function to unsubscribe when the component unmounts
    return () => {
      SupabaseClientService.removeChannel(customerTableChanges);
      SupabaseClientService.removeChannel(customersPaymentsTableChanges);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getBranches();
    fetchTapTapProfiles();

    fetchCustomers(true, roleBasedViewLabel);
  }, [roleBasedViewLabel]);

  const selectedFilterIndicator = useMemo(() => {
    const selectedQuickFilter = findSelectedQuickFilterViaParamV2(
      selectedFilter,
      selectedGetCustomerPayload.sort
    );
    return selectedDashboardView || selectedQuickFilter?.label || 'ALL ?';
  }, [selectedDashboardView, selectedFilter, selectedGetCustomerPayload.sort]);

  const visibleCustomerStatusKey = useMemo(
    () =>
      filteredCustomers
        .map((customer) => `${customer.id}:${customer.status}`)
        .join('|'),
    [filteredCustomers]
  );

  useEffect(() => {
    const statuses = STATUS_BY_VIEW[selectedFilterIndicator] || [];
    let isMounted = true;

    const fetchRunningDurationLogs = async () => {
      if (!statuses.length) {
        setRunningDurationLogMap({});
        return;
      }

      const { data, error } = await fetchTicketLogsByStatuses(statuses);

      if (error) {
        console.error('Failed to fetch running duration logs:', error);
        return;
      }

      // eslint-disable-next-line no-undef
      const latestLogMap = (data || []).reduce<Record<string, string>>(
        (acc, log: any) => {
          if (!log?.ticket_id || !log?.status || !log?.created_at) {
            return acc;
          }

          const key = `${log.ticket_id}__${log.status}`;
          const existingCreatedAt = acc[key];

          if (
            !existingCreatedAt ||
            new Date(log.created_at).getTime() >
              new Date(existingCreatedAt).getTime()
          ) {
            acc[key] = log.created_at;
          }

          return acc;
        },
        {}
      );

      if (isMounted) {
        setRunningDurationLogMap(latestLogMap);
      }
    };

    fetchRunningDurationLogs();

    return () => {
      isMounted = false;
    };
  }, [selectedFilterIndicator, visibleCustomerStatusKey]);

  const filteredCustomersWithRunningDurations = useMemo(
    () =>
      filteredCustomers.map((customer) => {
        const logCreatedAt =
          runningDurationLogMap[`${customer.id}__${customer.status}`];

        return {
          ...customer,
          running_status_started_at: logCreatedAt || customer.created_at,
          running_status_has_log: Boolean(logCreatedAt)
        };
      }),
    [filteredCustomers, runningDurationLogMap]
  );

  // eslint-disable-next-line no-unused-vars
  const handleScroll = (element: any) => {
    const bottom =
      Math.abs(
        element.target.scrollHeight -
          (element.target.scrollTop + element.target.clientHeight)
      ) <= 1;

    if (bottom) {
      fetchCustomersNextPage();
    }
  };

  const filterViewCheck = (view?: string | null) => {
    setSelectedDashboardView(view || null);

    if (view === 'CASHIER' && (!statusFilter || statusFilter !== 'payment')) {
      setStatusFilter('');
      setCourierFilter('');
    }
  };

  if (!showUi) {
    return <></>;
  }

  return (
    <Page className={classes.root} title="TapTap Customer">
      <LoaderBar isLoading={isCustomersLoading} />
      <Toolbar
        hasCustomers={hasCustomers}
        isLoading={isCustomersLoading}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        courierFilter={courierFilter}
        setCourierFilter={setCourierFilter}
        filterViewCheck={filterViewCheck}
        selectedDashboardView={selectedDashboardView}
      />
      {/* <Summary customers={filteredCustomers} isLoading={isCustomersLoading} /> */}

      <OperationDashboard
        customers={filteredCustomersWithRunningDurations}
        isCustomersLoading={isCustomersLoading}
        isNextPageLoading={isNextPageLoading}
        handleScroll={handleScroll}
        onEditPayment={setEditCustomerPaymentViaCustomerIDDialogVisible}
        isReadOnly={isTaptapDashboardReadOnly}
        includeAllTicketStatuses
        selectedDashboardView={selectedDashboardView}
        statusFilter={statusFilter}
      />

      {/* THIS IS TAPTAP V3 */}
      {/* ACCOUNTING/ CASHIER VIEW */}
      {/* {selectedFilterIndicator === quickFilterViewsLabels.CASHIER && (
        <TapTapCashierView
          customers={filteredCustomersWithRunningDurations}
          isCustomersLoading={isCustomersLoading}
          setEditCustomerPaymentViaCustomerIDDialogVisible={
            setEditCustomerPaymentViaCustomerIDDialogVisible
          }
          handleScroll={handleScroll}
        />
      )} */}

      {/* {selectedFilterIndicator === quickFilterViewsLabels.AFTER_CARE_QA && (
        <TapTapAfterCareQAView
          customers={filteredCustomersWithRunningDurations}
          isCustomersLoading={isCustomersLoading}
          handleScroll={handleScroll}
        />
      )} */}

      {/* ACCOUNTING VIEW */}
      {/* {selectedFilterIndicator ===
        quickFilterViewsLabels.PAYMENT_VERIFICATION && (
        <TapTapAccountingView
          customers={filteredCustomersWithRunningDurations}
          isCustomersLoading={isCustomersLoading}
          handleScroll={handleScroll}
          setEditCustomerPaymentViaCustomerIDDialogVisible={
            setEditCustomerPaymentViaCustomerIDDialogVisible
          }
        />
      )} */}

      {/*TECHNICAL DEPARTMENT VIEW*/}
      {/* {selectedFilterIndicator === quickFilterViewsLabels.FOR_BUILD && (
        <div style={{ padding: 20 }}>
          <Grid
            onScroll={handleScroll}
            container
            spacing={2}
            style={{
              maxHeight: '85vh',
              overflow: 'auto',
              opacity: isCustomersLoading ? 0.3 : 1
            }}
            direction="row"
            justifyContent="space-evenly"
            alignItems="stretch"
          >
            {filteredCustomersWithRunningDurations?.map(
              (customer: TapTapCustomerData, index: number) => (
                <TapTapForBuildCustomerItem
                  key={`${index}`}
                  customer={customer}
                />
              )
            )}
          </Grid>
        </div>
      )} */}

      {/* SALES DEPARTMENT VIEW*/}
      {/* {selectedFilterIndicator === quickFilterViewsLabels.SALES_VIEW && (
        <TapTapSalesDashboardView
          customers={filteredCustomersWithRunningDurations}
          isCustomersLoading={isCustomersLoading}
          handleScroll={handleScroll}
        />
      )} */}

      {/* WAREHOUSE DEPARTMENT view*/}
      {/* {selectedFilterIndicator === quickFilterViewsLabels.RELEASING && (
        <TapTapReleasingView
          customers={filteredCustomersWithRunningDurations}
          isCustomersLoading={isCustomersLoading}
          handleScroll={handleScroll}
        />
      )} */}

      {/* {canViewAllDashboard &&
      selectedFilterIndicator !== quickFilterViewsLabels.CASHIER &&
      selectedFilterIndicator !== quickFilterViewsLabels.AFTER_CARE_QA &&
      selectedFilterIndicator !== quickFilterViewsLabels.PAYMENT_VERIFICATION &&
      selectedFilterIndicator !== quickFilterViewsLabels.FOR_BUILD &&
      selectedFilterIndicator !== quickFilterViewsLabels.SALES_VIEW &&
      selectedFilterIndicator !== quickFilterViewsLabels.RELEASING ? (
        <TapTapAdminDashboard
          customers={filteredCustomersWithRunningDurations}
          isCustomersLoading={isCustomersLoading}
          isNextPageLoading={isNextPageLoading}
          handleScroll={handleScroll}
          onEditPayment={setEditCustomerPaymentViaCustomerIDDialogVisible}
          isReadOnly={readOnlyTaptapAdminDashboard}
        />
      ) : null} */}

      {!isTaptapDashboardReadOnly ? (
        <>
          <AddCustomerPaymentDialog
            customer={addCustomerPaymentViaCustomerIDDialogVisible}
            isVisible={
              addCustomerPaymentViaCustomerIDDialogVisible !== undefined
            }
            handleClose={() =>
              setAddCustomerPaymentViaCustomerIDDialogVisible(undefined)
            }
          />
          <TapTapCustomerPaymentDetailsDialog
            paymentDetails={editCustomerPaymentViaCustomerIDDialogVisible}
            isVisible={
              editCustomerPaymentViaCustomerIDDialogVisible !== undefined
            }
            handleClose={() =>
              setEditCustomerPaymentViaCustomerIDDialogVisible(undefined)
            }
          />
        </>
      ) : null}

      <AlertDialog
        isVisible={!taptapVersion || taptapVersion < currentTapTapVersion}
        title="TapTap Version Outdated"
        subTitle={`Press "OK" to update. Then refresh the page please :D`}
        handleClose={() => {
          dispatch(taptapCustomerActions.resetAll());
          dispatch(taptapCustomerActions.taptapFetchCustomersThunk());
        }}
      />
    </Page>
  );
};

export default TapTapViewV2;
